import { Stack, TextField } from '@mui/material';
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { GridColDef } from '@mui/x-data-grid';
import { CrudPage } from '../../shared/components/CrudPage';
import { EntitySelect } from '../../shared/components/EntitySelect';
import { resources } from '../../core/services/resources';
import type { Item, ItemDetail } from '../../core/types/data-models';

/**
 * Flat list of all detail rows. Each row is 1-to-1 with an Item — the
 * `itemId` column has a unique constraint at the DB level, so attempting
 * to create a second detail for the same item will surface as a 409.
 */
export function ItemDetailsPage() {
  const itemsLookup = useQuery({
    queryKey: ['items', 'select'],
    queryFn: () => resources.items.list({ pageSize: 200, sort: 'name' }),
  });
  const itemName = useMemo(() => {
    const m = new Map<number, string>();
    for (const it of itemsLookup.data?.data ?? []) m.set(it.id, it.name);
    return m;
  }, [itemsLookup.data]);

  const columns: GridColDef[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'itemId',
        headerName: 'Item',
        width: 220,
        renderCell: (p) => itemName.get(p.value as number) ?? `#${p.value}`,
      },
      { field: 'priority', headerName: 'Priority', width: 110, type: 'number' },
      { field: 'notes', headerName: 'Notes', flex: 1, minWidth: 240 },
    ],
    [itemName],
  );

  return (
    <CrudPage<ItemDetail>
      title="Item details"
      entityName="Item detail"
      description="1-to-1 extension rows for items. The `itemId` is unique — each item has at most one detail."
      cacheKey={['item-details']}
      service={resources.itemDetails}
      columns={columns}
      defaultValues={{ notes: '', priority: 0, itemId: undefined }}
      renderForm={({ values, setValues }) => (
        <Stack spacing={2}>
          <EntitySelect<Item>
            label="Item"
            required
            helperText="The item this detail belongs to (1-to-1)."
            cacheKey={['items', 'select']}
            service={resources.items}
            value={values.itemId ?? null}
            onChange={(id) => setValues({ ...values, itemId: id ?? undefined })}
          />
          <TextField
            label="Notes"
            multiline
            minRows={3}
            value={values.notes ?? ''}
            onChange={(e) => setValues({ ...values, notes: e.target.value })}
          />
          <TextField
            label="Priority"
            type="number"
            value={values.priority ?? 0}
            onChange={(e) => setValues({ ...values, priority: Number(e.target.value) || 0 })}
          />
        </Stack>
      )}
    />
  );
}
